// packages/web-ui/src/AppShell/ActivityBar.tsx
//
// W2-S3 (AC #1, #2, #4) — populated ActivityBar with top-cluster
// destination buttons (Chat / KB / Files / Views / Diff / Git / Skills), a divider, and a
// bottom-anchored utility cluster (Settings / Help / Account).
//
// Source of truth:
//   - Button table:      AC #1 + UX §4.2 + UX §4.3
//   - Active styling:    AC #1 + UX §4.1 + UX §4.7 (embed → stripe-only)
//   - ARIA + focus:      AC #2 + UX §4.6
//   - Badge slot:        AC #4 + UX §4.5
//   - Width/orientation: AC #1 + W2-S2 RB-locked answer #1
//
// Pitfall #4 — DO NOT add a second `aria-live` announcer; W2-S2's AppShell
// already provides one. We only render buttons here.

import { useState, type JSX, type CSSProperties, type ComponentType } from 'react';
import {
  MessageSquare,
  Network,
  FolderOpen,
  LayoutGrid,
  GitCompare,
  GitBranch,
  RotateCcw,
  Settings,
  User,
  Zap,
} from 'lucide-react';
import type { Breakpoint } from './responsive-tokens.js';
import { PANEL_DEFAULT_WIDTHS, Z_INDEX } from './responsive-tokens.js';
import type { ActiveDestination, ClientMode } from '../state/navigation.js';
import { useRootStore } from '../state/store.js';
import { navigateTo } from '../router/hash-router.js';
import { openNewChat } from '../state/open-new-chat.js';
import { getDefaultApiContext } from '../api/client.js';
import { resetWorkspaceApi } from '../api/clients/auth.js';
import { useCapabilities } from '../hooks/useCapabilities.js';
import {
  ActivityBarBadge,
  type ActivityBarBadgeKind,
} from '../components/badges/ActivityBarBadge.js';
import { HelpPopover } from './HelpPopover.js';
import { KeyboardSafeTooltip } from './KeyboardSafeTooltip.js';

export interface ActivityBarProps {
  breakpoint: Breakpoint;
  clientMode: ClientMode;
}

interface DestinationDescriptor {
  key: ActiveDestination;
  label: string;
  shortcut: string;
  Icon: ComponentType<{ size?: number; 'aria-hidden'?: boolean }>;
}

/** Git workspace iteration: add Git as a first-class destination in the rail. */
const TOP_CLUSTER: readonly DestinationDescriptor[] = [
  { key: 'chat', label: 'Chat', shortcut: 'Ctrl+1', Icon: MessageSquare },
  { key: 'kb', label: 'KB Browser', shortcut: 'Ctrl+2', Icon: Network },
  { key: 'files', label: 'Files', shortcut: 'Ctrl+3', Icon: FolderOpen },
  { key: 'views', label: 'Views', shortcut: 'Ctrl+4', Icon: LayoutGrid },
  { key: 'diff', label: 'Diff', shortcut: 'Ctrl+5', Icon: GitCompare },
  { key: 'git', label: 'Git', shortcut: 'Ctrl+6', Icon: GitBranch },
  { key: 'skills', label: 'Skills', shortcut: 'Ctrl+7', Icon: Zap },
];

interface BadgeData {
  kind: ActivityBarBadgeKind;
  count?: number;
  /** Optional aria-label augmentation reflecting the badge meaning. */
  ariaSuffix?: string;
}

/**
 * Read a numeric probe field from a nested slice on the root state. Returns
 * 0 when either the parent slice or the leaf field is missing or non-numeric.
 * Supports the legacy "synthetic" injection shape used by the W2-S3 AC #4
 * tests (`useRootStore.setState({views: {pendingCount: 4}})`) without
 * collapsing the lookup against arbitrary state.
 */
function pickProbeNumber<S>(
  state: S,
  parent: 'chat' | 'kb' | 'files' | 'views' | 'changeset',
  leaf: string,
): number {
  const root = state as unknown as Record<string, unknown>;
  const slice = root[parent];
  if (slice === null || typeof slice !== 'object') return 0;
  const v = (slice as Record<string, unknown>)[leaf];
  return typeof v === 'number' ? v : 0;
}

/**
 * Reads the badge state for a destination. Per AC #4, slices that have not
 * yet been populated by downstream stories return zeros and the badge stays
 * hidden.
 *
 * W2 fix-pass HIGH-1 — every probe now goes through `useRootStore((s) => …)`
 * selectors so re-renders fire when the underlying slice fields change. Two
 * shapes are supported in the same selector:
 *   1. The pre-W2-fix nested probe shape (`s.views.pendingCount`,
 *      `s.chat.unreadCount`, …) used by the existing AC #4 test that
 *      injects `{views: {pendingCount: 4}}` directly into root state.
 *   2. Derived counts from the real slice fields (`pendingViews.length`,
 *      etc.) so badges actually react to real activity once W4-S3 / W4-S4
 *      slices are populated.
 *
 * The pre-fix-pass implementation snapshotted the entire state once per render
 * via `useRootStore.getState()` — slice updates never triggered a re-render
 * of `<DestinationButton>`, so the badge data was frozen at first mount.
 */
function useBadgeFor(destination: ActiveDestination): BadgeData | null {
  const unreadCount = useRootStore((s) =>
    pickProbeNumber(s, 'chat', 'unreadCount'),
  );
  const indexStatus = useRootStore(
    (s) => (s as unknown as { kb?: { indexStatus?: 'ok' | 'error' } }).kb?.indexStatus,
  );
  const filesErrorCount = useRootStore((s) =>
    pickProbeNumber(s, 'files', 'errorCount'),
  );
  const filesDirtyCount = useRootStore((s) =>
    pickProbeNumber(s, 'files', 'dirtyCount'),
  );
  // Pending-view badge is permanently zero: views are now auto-saved by the
  // server before `kb:viewRendered` fires, so there is no pending queue.
  // The selector is kept for the AC #4 nested-probe shape that some tests
  // still inject (`{views: {pendingCount: 4}}`) — it returns that probe
  // when present and zero otherwise.
  const viewsPendingCount = useRootStore((s) =>
    pickProbeNumber(s, 'views', 'pendingCount'),
  );
  const changesetActiveCount = useRootStore((s) =>
    pickProbeNumber(s, 'changeset', 'activeCount'),
  );
  const changesetFailedCount = useRootStore((s) =>
    pickProbeNumber(s, 'changeset', 'failedCount'),
  );

  // Capability: hard-coded to the W2-S3 default (no <CapabilityProvider> yet).
  // W3-S6 replaces this with /api/v1/health polling.
  const hasKbConfig = true;

  switch (destination) {
    case 'chat': {
      return unreadCount > 0
        ? { kind: 'numeric', count: unreadCount, ariaSuffix: `${unreadCount} unread` }
        : null;
    }
    case 'kb': {
      if (indexStatus === 'error') {
        return { kind: 'error', ariaSuffix: 'index error' };
      }
      if (!hasKbConfig) {
        return { kind: 'warning', ariaSuffix: 'knowledge base not configured' };
      }
      return null;
    }
    case 'files': {
      if (filesErrorCount > 0) return { kind: 'error', ariaSuffix: 'validation errors' };
      if (filesDirtyCount > 0) return { kind: 'warning', ariaSuffix: 'unsaved changes' };
      return null;
    }
    case 'views': {
      return viewsPendingCount > 0
        ? {
            kind: 'numeric',
            count: viewsPendingCount,
            ariaSuffix: `${viewsPendingCount} pending response-views`,
          }
        : null;
    }
    case 'diff': {
      if (changesetFailedCount > 0)
        return { kind: 'error', ariaSuffix: `${changesetFailedCount} failed entities` };
      if (changesetActiveCount > 0) {
        return {
          kind: 'numeric',
          count: changesetActiveCount,
          ariaSuffix: `${changesetActiveCount} pending changesets`,
        };
      }
      return null;
    }
    case 'settings':
      return null;
    case 'git':
      return null;
    case 'skills':
      return null;
  }
}

export function ActivityBar({ breakpoint, clientMode }: ActivityBarProps): JSX.Element {
  const isEmbedNarrow = breakpoint === 'embed-narrow';
  const isEmbedded = clientMode === 'embedded';
  const activeDestination = useRootStore((s) => s.activeDestination);

  const railThickness = isEmbedded
    ? PANEL_DEFAULT_WIDTHS.activityBarEmbed
    : PANEL_DEFAULT_WIDTHS.activityBar;

  const style: CSSProperties = isEmbedNarrow
    ? {
        height: railThickness,
        width: '100%',
        zIndex: Z_INDEX.baseCanvas,
      }
    : {
        width: railThickness,
        height: '100%',
        zIndex: Z_INDEX.baseCanvas,
      };

  const orientationClass = isEmbedNarrow
    ? 'flex flex-row items-center overflow-x-auto'
    : 'flex flex-col items-center';

  // Divider: 1 px hairline. Vertical orientation in horizontal layout, vice versa.
  const dividerClass = isEmbedNarrow ? 'w-px h-6 mx-2 bg-taupe-300' : 'h-px w-6 my-2 bg-taupe-300';

  return (
    <nav
      role="navigation"
      aria-label="Primary destinations"
      data-testid="activity-bar"
      data-orientation={isEmbedNarrow ? 'horizontal' : 'vertical'}
      data-breakpoint={breakpoint}
      data-client-mode={clientMode}
      className={`bg-cream-50 border-taupe-300 ${orientationClass} ${
        isEmbedNarrow ? 'border-b' : 'border-r'
      }`}
      style={style}
    >
      {TOP_CLUSTER.map((dest) => (
        <DestinationButton
          key={dest.key}
          descriptor={dest}
          isActive={activeDestination === dest.key}
          isEmbedded={isEmbedded}
          isEmbedNarrow={isEmbedNarrow}
        />
      ))}

      <div role="separator" className={dividerClass} />

      {/* Bottom cluster — gap-pushed to the end of the rail in vertical
          layouts via `mt-auto` on the Settings button. */}
      <div
        data-testid="activity-bar-bottom-cluster"
        className={
          isEmbedNarrow
            ? 'flex flex-row items-center'
            : 'mt-auto flex flex-col items-center'
        }
      >
        <ResetWorkspaceButton isEmbedded={isEmbedded} />
        <DestinationButton
          descriptor={{
            key: 'settings',
            label: 'Settings',
            shortcut: 'Ctrl+,',
            Icon: Settings,
          }}
          isActive={activeDestination === 'settings'}
          isEmbedded={isEmbedded}
          isEmbedNarrow={isEmbedNarrow}
        />
        <HelpPopover />
        {isEmbedded ? null : (
          <button
            type="button"
            disabled
            data-testid="activity-bar-account"
            aria-label="Account (coming soon)"
            title="Single-tenant — auth deferred. Click to open Settings → Account."
            className="relative flex h-9 w-9 items-center justify-center rounded text-taupe-500 outline-none disabled:cursor-not-allowed disabled:opacity-60"
          >
            <User size={20} aria-hidden="true" />
          </button>
        )}
      </div>
    </nav>
  );
}

interface DestinationButtonProps {
  descriptor: DestinationDescriptor;
  isActive: boolean;
  isEmbedded: boolean;
  isEmbedNarrow: boolean;
}

function DestinationButton({
  descriptor,
  isActive,
  isEmbedded,
  isEmbedNarrow,
}: DestinationButtonProps): JSX.Element {
  const { key, label, shortcut, Icon } = descriptor;
  const badge = useBadgeFor(key);

  const ariaLabel = badge?.ariaSuffix !== undefined ? `${label} (${badge.ariaSuffix})` : label;

  // Active-state classes:
  //   - Standalone: cream-200 fill + 3 px taupe-700 left-edge stripe.
  //   - Embedded:   stripe only (no fill) per UX §4.7.
  // Stripe orientation flips for horizontal embed-narrow layout.
  // Audi-pass: active stripe is now the signature `--color-audi-red`,
  // not muted taupe — gives the activity bar its "brand moment" and
  // mirrors audi.com's red active-state marker.
  const isHorizontal = isEmbedNarrow;
  const stripeClass = isHorizontal
    ? 'before:bg-audi-red before:absolute before:bottom-0 before:left-0 before:h-[3px] before:w-full'
    : 'before:bg-audi-red before:absolute before:left-0 before:top-0 before:h-full before:w-[3px]';
  const fillClass = !isEmbedded ? 'bg-cream-200' : '';
  const activeClasses = isActive ? `${stripeClass} ${fillClass}` : '';

  // Hover fill — standalone: cream-100; embed: cream-50/40.
  const hoverClass = isEmbedded ? 'hover:bg-cream-50/40' : 'hover:bg-cream-100';

  const sizeClass = isEmbedded ? 'h-9 w-9' : 'h-9 w-9';

  // AC #7 retrofit (UX §4.4): wrap the trigger in KeyboardSafeTooltip so
  // hover-on-pointer shows `<label> <sub>{shortcut}</sub>` after a 600 ms
  // delay; keyboard-driven focus is skipped (existing aria-label still
  // announces the destination + shortcut for AT users).
  return (
    <KeyboardSafeTooltip label={label} shortcut={shortcut}>
      <button
        type="button"
        data-testid={`activity-bar-${key}`}
        aria-label={ariaLabel}
        aria-pressed={isActive}
        onClick={() => {
          // "Chat" opens a fresh conversation (reusing an open blank one) rather
          // than routing to the contentless bare `/chat` placeholder tab.
          if (key === 'chat') {
            void openNewChat();
            return;
          }
          navigateTo({ kind: key });
        }}
        className={`text-fg focus-visible:ring-taupe-900 relative flex items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${sizeClass} ${hoverClass} ${activeClasses}`}
      >
        <Icon size={20} aria-hidden={true} />
        {badge !== null ? <ActivityBarBadge kind={badge.kind} count={badge.count} /> : null}
      </button>
    </KeyboardSafeTooltip>
  );
}

// Quick-action button for the bottom cluster. Wipes the current workspace
// and restores the `e2e/fixtures/debug-workspace/` fixture, then reloads
// the page so every panel re-fetches against the fresh state. Available to
// any authenticated user (single-tenant deployment; confirm-dialog plus the
// server's confirmWorkspaceName check guard against accidental fire).
function ResetWorkspaceButton({ isEmbedded }: { isEmbedded: boolean }): JSX.Element {
  const { capabilities } = useCapabilities();
  const [busy, setBusy] = useState(false);

  const workspaceRoot = capabilities?.workspaceRoot ?? '';
  const workspaceName =
    workspaceRoot.length > 0
      ? (workspaceRoot.replace(/\\/g, '/').split('/').filter((s) => s.length > 0).pop() ?? '')
      : '';

  async function onClick(): Promise<void> {
    if (workspaceName.length === 0) {
      window.alert('Workspace not detected — cannot reset.');
      return;
    }
    const proceed = window.confirm(
      `Reset workspace "${workspaceName}" to the debug-workspace fixture?\n\n` +
        'This wipes all .sysml files, chat sessions, views, and pending changesets in the ' +
        'current workspace, then restores the canonical e2e/fixtures/debug-workspace/ ' +
        'content. User accounts are preserved.\n\n' +
        'The page will reload when the reset completes.',
    );
    if (!proceed) return;
    setBusy(true);
    const res = await resetWorkspaceApi(getDefaultApiContext(), {
      confirmWorkspaceName: workspaceName,
    });
    if (res.ok) {
      window.location.reload();
      return;
    }
    setBusy(false);
    window.alert(`Reset failed: ${res.error.message}`);
  }

  const sizeClass = 'h-9 w-9';
  const hoverClass = isEmbedded ? 'hover:bg-cream-50/40' : 'hover:bg-cream-100';

  return (
    <KeyboardSafeTooltip label="Reset workspace">
      <button
        type="button"
        data-testid="activity-bar-reset-workspace"
        aria-label={
          workspaceName.length > 0
            ? `Reset workspace "${workspaceName}" to debug-workspace fixture`
            : 'Reset workspace to debug-workspace fixture'
        }
        disabled={busy || workspaceName.length === 0}
        title="Reset workspace (admin / testing only)"
        onClick={() => {
          void onClick();
        }}
        className={`text-fg focus-visible:ring-taupe-900 relative flex items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40 ${sizeClass} ${hoverClass}`}
      >
        <RotateCcw size={20} aria-hidden={true} />
      </button>
    </KeyboardSafeTooltip>
  );
}
